import { useCallback, useMemo } from 'react';

import { EmptyImage } from 'app/components/commons/Images/EmptyImage/EmptyImage';
import { SingleImage } from 'app/components/commons/Images/SingleImage/SingleImage';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import { useRoomCategories } from 'app/hooks/data/rooms/useRoomCategories';
import type { BedCategory, RoomCategory } from 'app/typings';
import { ROOM_STATUS } from 'app/typings/core/admin/rooms/enum/roomStatus';

import type { HotelRoom } from '../_types';

import { getRoomCapacityLabel, getRoomPath } from './_utils/roomUtils';
import { EntityRow } from './entity/EntityRow';
import { EntitySection } from './entity/EntitySection';
import { EntityTable } from './entity/EntityTable';
import type { EntityRowConfig, EntitySectionConfig } from './entity/entity';
import { SortableSection } from './sortable/SortableSection';

type Props = {
  rooms: HotelRoom[];
  isLegacy: boolean;
  onCreateRoom: () => void;
  onPublish: (room: HotelRoom) => void;
  onUnpublish: (room: HotelRoom) => void;
  onReorder?: (sectionKey: string, items: HotelRoom[]) => void;
};

const COVER_IMAGE_SIZE = 40;

const getRowConfig = (
  room: HotelRoom,
  isLegacy: boolean,
  handlers: Pick<Props, 'onPublish' | 'onUnpublish'>,
  bedCategories?: BedCategory[],
  roomCategories?: RoomCategory[]
): EntityRowConfig => {
  const categoryName =
    roomCategories?.find((category) => category.id === room.categoryId)?.name ??
    '';

  const displayName = room.name ?? categoryName ?? 'Untitled';
  const secondaryLabel = room.name?.trim() ? categoryName : undefined;
  const coverPicture = room.pictures?.[0];

  return {
    id: room.id,
    cover: coverPicture ? (
      <SingleImage
        image={coverPicture}
        width={COVER_IMAGE_SIZE}
        height={COVER_IMAGE_SIZE}
      />
    ) : (
      <EmptyImage width={COVER_IMAGE_SIZE} height={COVER_IMAGE_SIZE} />
    ),
    primaryLabel: displayName,
    secondaryLabel,
    roomCapacityLabel: getRoomCapacityLabel(room, bedCategories),
    status: room.status,
    redirectPath: getRoomPath(room, isLegacy),
    actions: [
      room.status !== ROOM_STATUS.PUBLISHED && {
        key: 'publish',
        label: 'Publish',
        onClick: () => handlers.onPublish(room),
      },
      room.status !== ROOM_STATUS.UNPUBLISHED && {
        key: 'unpublish',
        label: 'Unpublish',
        onClick: () => handlers.onUnpublish(room),
      },
    ].filter(Boolean) as EntityRowConfig['actions'],
  };
};

export const RoomsTable = ({
  rooms,
  isLegacy,
  onCreateRoom,
  onPublish,
  onUnpublish,
  onReorder,
}: Props) => {
  const handlers = useMemo(
    () => ({ onPublish, onUnpublish }),
    [onPublish, onUnpublish]
  );

  const { data: bedCategories } = useBedCategories();
  const { data: roomCategories } = useRoomCategories();

  const renderSection = useCallback(
    (section: EntitySectionConfig<HotelRoom>) => {
      const rowConfigGetter = (room: HotelRoom) =>
        getRowConfig(room, isLegacy, handlers, bedCategories, roomCategories);

      if (onReorder) {
        return (
          <SortableSection
            key={section.key}
            section={section}
            getId={(room) => room.id}
            renderItem={(room, { handleRef, ...restDragHandleProps }) => (
              <EntityRow
                tableType="room"
                config={rowConfigGetter(room)}
                handleRef={handleRef}
                dragHandleProps={restDragHandleProps}
                isDraggable={true}
              />
            )}
            onReorder={onReorder}
            emptyLabel="No rooms"
            emptyDescription="No rooms have been created for this hotel yet."
          />
        );
      }

      return (
        <EntitySection
          key={section.key}
          section={section}
          renderItem={(room) => (
            <EntityRow
              tableType="room"
              key={room.id}
              config={rowConfigGetter(room)}
            />
          )}
          emptyLabel="No rooms"
          emptyDescription="No rooms have been created for this hotel yet."
        />
      );
    },
    [onReorder, isLegacy, handlers, bedCategories, roomCategories]
  );

  return (
    <EntityTable
      title="Rooms"
      createLabel="Create new room"
      sections={[{ items: rooms, key: 'all-rooms' }]}
      renderSection={renderSection}
      onCreate={onCreateRoom}
    />
  );
};
